/* Harber web push (our own, replacing PushOwl). Loaded by layout/theme.liquid when the theme setting "Web push"
 * is Test or Live. The service worker and the API live behind the app proxy (/apps/harber/push/*, harberapp backend).
 *
 * - Visitors subscribe from the bell (bottom right, above the chat) or the short card that offers it once.
 * - A browser that already allowed notifications (e.g. for PushOwl) is moved over silently: permission belongs to the
 *   site, so no new prompt. In Live mode PushOwl's own service worker is then removed from that browser.
 * - Subscribed browsers report product views and their bag (abandoned cart / browse reminders).
 * - Test mode (setting "Test"): only browsers opened once with ?push-test=1 take part. ?push-test=1 in Live marks a
 *   browser as a test browser too (it receives the admin's "Send test"). */
/* A page the browser only prerenders (hover on a product link, theme.liquid speculation rules) hasn't been seen:
 * nothing runs until it is actually shown. */
((run) => (document.prerendering ? document.addEventListener('prerenderingchange', run, { once: true }) : run()))(() => {
  const cfg = window.Theme && Theme.push;
  if (!cfg || !('serviceWorker' in navigator)) return;
  // iPhone/iPad (iOS 16.4+): web push only works for the site added to the Home Screen. In Safari the bell explains
  // how; opened from the Home Screen (standalone) the normal flow below runs. The manifest makes it an app.
  const ua = navigator.userAgent;
  const iosVer = /iP(hone|ad|od)/.test(ua) ? /OS (\d+)_(\d+)/.exec(ua) : null;
  const ios = Boolean(iosVer) && (Number(iosVer[1]) > 16 || (Number(iosVer[1]) === 16 && Number(iosVer[2]) >= 4));
  const standalone = navigator.standalone === true || matchMedia('(display-mode: standalone)').matches;
  if (ios && !document.querySelector('link[rel="manifest"]')) {
    const l = document.createElement('link');
    l.rel = 'manifest';
    l.href = '/apps/harber/push/manifest.webmanifest';
    document.head.appendChild(l);
  }
  // In-app browsers (Instagram, Facebook…) can't add to the Home Screen.
  const iosHint = ios && !standalone && !('PushManager' in window) && !/FBAN|FBAV|Instagram|Line\/|GSA\//.test(ua);
  if (!iosHint && (!('PushManager' in window) || !('Notification' in window))) return;

  const BASE = '/apps/harber/push';
  const KEY = 'harber:push';
  const store = {
    get(k) { try { return JSON.parse(localStorage.getItem(k) || 'null'); } catch { return null; } },
    set(k, v) { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* private mode */ } },
    del(k) { try { localStorage.removeItem(k); } catch { /* private mode */ } },
  };
  try {
    const q = new URLSearchParams(location.search).get('push-test');
    if (q === '1') store.set('harber:push-test', 1);
    if (q === '0') store.del('harber:push-test');
  } catch { /* old browser */ }
  const isTest = Boolean(store.get('harber:push-test'));
  if (cfg.mode === 'test' && !isTest) return;
  const S = cfg.strings || {};

  const post = (path, body) => fetch(BASE + path, {
    method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json', accept: 'application/json' }, body: JSON.stringify(body), keepalive: true,
  });
  const ctx = () => ({ lang: Theme.locale, root: (Theme.routes && Theme.routes.root) || '/', country: Theme.country });
  const browserName = () => {
    const ua = navigator.userAgent;
    const os = /iPhone|iPad/.test(ua) ? 'iOS' : /Android/.test(ua) ? 'Android' : /Mac/.test(ua) ? 'macOS' : /Windows/.test(ua) ? 'Windows' : '';
    const b = /Edg\//.test(ua) ? 'Edge' : /Firefox\//.test(ua) ? 'Firefox' : /Chrome\//.test(ua) ? 'Chrome' : /Safari\//.test(ua) ? 'Safari' : 'Browser';
    return `${b}${os ? ` · ${os}` : ''}`;
  };
  const keyBytes = (b64) => {
    const s = (b64 + '='.repeat((4 - (b64.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/');
    return Uint8Array.from(atob(s), (c) => c.charCodeAt(0));
  };

  let regPromise = null;
  const registration = () => {
    regPromise = regPromise || navigator.serviceWorker.register(`${BASE}/sw.js`, { scope: `${BASE}/` }).then((reg) => {
      const w = reg.installing || reg.waiting;
      if (reg.active || !w) return reg;
      return new Promise((done) => {
        w.addEventListener('statechange', () => { if (w.state === 'activated') done(reg); });
        setTimeout(() => done(reg), 8000);
      });
    });
    return regPromise;
  };
  const publicKey = async () => {
    const cached = store.get('harber:push-key');
    if (cached && cached.t > Date.now() - 7 * 864e5) return cached.k;
    const r = await fetch(`${BASE}/config`, { headers: { accept: 'application/json' } });
    const j = await r.json();
    if (!j.publicKey) throw new Error('no key');
    store.set('harber:push-key', { k: j.publicKey, t: Date.now() });
    return j.publicKey;
  };

  /** PushOwl's service worker(s) in this browser: unsubscribed and removed once we own the subscription (Live only). */
  const retirePushOwl = async () => {
    if (cfg.mode !== 'live') return false;
    const regs = await navigator.serviceWorker.getRegistrations().catch(() => []);
    let found = false;
    for (const r of regs) {
      const url = (r.active || r.waiting || r.installing || {}).scriptURL || '';
      if (!/pushowl|brevo-sw|\/apps\/sendinblue\//i.test(url)) continue;
      found = true;
      try { const s = await r.pushManager.getSubscription(); if (s) await s.unsubscribe(); } catch { /* ignore */ }
      try { await r.unregister(); } catch { /* ignore */ }
    }
    return found;
  };
  const hasPushOwl = async () => {
    const regs = await navigator.serviceWorker.getRegistrations().catch(() => []);
    return regs.some((r) => /pushowl|brevo-sw/i.test(((r.active || r.waiting || r.installing || {}).scriptURL) || ''));
  };

  let saved = store.get(KEY);
  const subscribe = async (source) => {
    const reg = await registration();
    const key = await publicKey();
    let sub = await reg.pushManager.getSubscription();
    if (sub && saved && saved.k && saved.k !== key) { await sub.unsubscribe(); sub = null; }
    if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(key) });
    const res = await post('/subscribe', {
      subscription: sub.toJSON(), ...ctx(), timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, source, test: isTest, browser: browserName(),
    });
    if (!res.ok) throw new Error(`subscribe ${res.status}`);
    const j = await res.json();
    saved = { id: j.id, s: j.secret, ep: sub.endpoint, k: key, t: Date.now() };
    store.set(KEY, saved);
    store.del('harber:push-off');
    return saved;
  };

  /** Off from the account: this browser's subscription is ended here and in the backend. */
  const unsubscribe = async () => {
    store.set('harber:push-off', 1);
    try {
      const reg = await registration();
      const sub = await reg.pushManager.getSubscription();
      if (sub) await sub.unsubscribe();
    } catch { /* ignore */ }
    if (saved) await post('/unsubscribe', { id: saved.id, secret: saved.s }).catch(() => {});
    store.del(KEY);
    saved = null;
  };

  /* Account → My details: "Browser notifications" switch */
  const accountSwitch = () => {
    const row = document.querySelector('[data-push-row]');
    const sw = row && row.querySelector('[data-push-switch]');
    if (!sw) return;
    const hint = row.querySelector('[data-push-hint]');
    const paint = () => {
      const denied = Notification.permission === 'denied';
      sw.setAttribute('aria-checked', String(Boolean(saved) && Notification.permission === 'granted'));
      sw.disabled = denied;
      if (denied && hint && hint.dataset.blocked) hint.textContent = hint.dataset.blocked;
    };
    row.hidden = false;
    paint();
    sw.addEventListener('click', async () => {
      const on = sw.getAttribute('aria-checked') !== 'true';
      sw.setAttribute('aria-busy', 'true');
      try {
        if (on) {
          const p = Notification.permission === 'granted' ? 'granted' : await Notification.requestPermission();
          if (p === 'granted') {
            await subscribe('account');
            if (bell) bell.hidden = true;
            startTracking();
          }
        } else {
          await unsubscribe();
        }
      } catch { /* the switch shows the real state below */ }
      sw.removeAttribute('aria-busy');
      paint();
    });
  };

  // ---- Tracking (subscribed browsers only)
  const track = async (extra) => {
    if (!saved) return;
    try {
      const r = await post('/track', { id: saved.id, secret: saved.s, ...ctx(), ...extra });
      if (r.status === 404) { store.del(KEY); saved = null; }
    } catch { /* offline */ }
  };
  const sendCart = async () => {
    try {
      const cart = await (await fetch('/cart.js', { headers: { accept: 'application/json' } })).json();
      // The personalisation fee lines are not "the product" of the reminder.
      const first = (cart.items || []).find((i) => !/^(leather-customization|deboss)$/.test(i.handle)) || (cart.items || [])[0];
      const img = first && (first.featured_image && first.featured_image.url || first.image);
      track({ cart: {
        token: cart.token, count: cart.item_count, total: cart.total_price / 100, currency: cart.currency,
        title: first ? first.product_title : null, image: img ? (img.startsWith('//') ? `https:${img}` : img) : null,
        url: first ? new URL(first.url, location.origin).href : null,
        productIds: (cart.items || []).map((i) => String(i.product_id)).slice(0, 50),
      } });
    } catch { /* ignore */ }
  };
  let cartTimer = 0;
  let tracking = false;
  const startTracking = () => {
    if (!saved || tracking) return;
    tracking = true;
    if (cfg.product) {
      const seen = store.get('harber:push-seen') || {};
      const now = Date.now();
      if (!seen[cfg.product.productId] || now - seen[cfg.product.productId] > 10 * 6e4) {
        seen[cfg.product.productId] = now;
        Object.keys(seen).forEach((k) => { if (now - seen[k] > 864e5) delete seen[k]; });
        store.set('harber:push-seen', seen);
        track({ view: cfg.product });
      }
    }
    try {
      if (!sessionStorage.getItem('harber:push-cart')) { sessionStorage.setItem('harber:push-cart', '1'); sendCart(); }
    } catch { sendCart(); }
    document.addEventListener('cart:updated', () => { clearTimeout(cartTimer); cartTimer = setTimeout(sendCart, 1500); });
  };

  // ---- UI: bell + card
  let bell = null;
  let card = null;
  const icon = '<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M6 8a6 6 0 1 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/></svg>';
  const closeCard = (snooze) => {
    if (!card) return;
    const c = card;
    card = null;
    c.classList.remove('is-open');
    setTimeout(() => c.remove(), 600);
    if (bell) bell.setAttribute('aria-expanded', 'false');
    if (snooze) store.set('harber:push-snooze', Date.now());
  };
  const showIosCard = (auto) => {
    if (card) return closeCard(false);
    card = document.createElement('div');
    card.className = 'push-card';
    card.setAttribute('role', 'dialog');
    card.setAttribute('aria-labelledby', 'PushCardTitle');
    card.innerHTML = `<button type="button" class="push-card__close" aria-label="${S.close || 'Close'}">×</button>
      <p class="push-card__eyebrow">${S.eyebrow || ''}</p>
      <p class="push-card__title" id="PushCardTitle">${S.iosTitle}</p>
      <ol class="push-card__steps"><li>${S.iosStep1}</li><li>${S.iosStep2}</li><li>${S.iosStep3}</li></ol>
      <div class="push-card__actions"><button type="button" class="btn btn--primary push-card__ok">${S.iosOk}</button></div>`;
    document.body.appendChild(card);
    requestAnimationFrame(() => { if (!card) return; card.getBoundingClientRect(); card.classList.add('is-open'); }); // the start state is laid out first, so it eases in
    if (bell) bell.setAttribute('aria-expanded', 'true');
    if (auto) card.dataset.auto = '1';
    card.querySelector('.push-card__close').addEventListener('click', () => closeCard(auto));
    card.querySelector('.push-card__ok').addEventListener('click', () => closeCard(true));
  };
  const showCard = (auto) => {
    if (iosHint) return showIosCard(auto);
    if (card) return closeCard(false);
    const denied = Notification.permission === 'denied';
    card = document.createElement('div');
    card.className = 'push-card';
    card.setAttribute('role', 'dialog');
    card.setAttribute('aria-labelledby', 'PushCardTitle');
    card.innerHTML = `<button type="button" class="push-card__close" aria-label="${S.close || 'Close'}">×</button>
      <p class="push-card__eyebrow">${S.eyebrow || ''}</p>
      <p class="push-card__title" id="PushCardTitle">${denied ? S.blockedTitle : S.title}</p>
      <p class="push-card__text">${denied ? S.blocked : S.text}</p>
      ${denied ? '' : `<div class="push-card__actions"><button type="button" class="btn btn--primary push-card__allow">${S.allow}</button><button type="button" class="push-card__later">${S.later}</button></div>`}`;
    document.body.appendChild(card);
    requestAnimationFrame(() => { if (!card) return; card.getBoundingClientRect(); card.classList.add('is-open'); }); // the start state is laid out first, so it eases in
    if (bell) bell.setAttribute('aria-expanded', 'true');
    if (auto) card.dataset.auto = '1';
    card.querySelector('.push-card__close').addEventListener('click', () => closeCard(auto));
    const later = card.querySelector('.push-card__later');
    if (later) later.addEventListener('click', () => closeCard(true));
    const allow = card.querySelector('.push-card__allow');
    if (allow) {
      allow.addEventListener('click', async () => {
        allow.disabled = true;
        try {
          const p = await Notification.requestPermission();
          if (p !== 'granted') { closeCard(true); if (p === 'denied') store.set('harber:push-snooze', Date.now() + 365 * 864e5); return; }
          await subscribe(auto ? 'prompt' : 'bell');
          card.querySelector('.push-card__title').textContent = S.doneTitle;
          card.querySelector('.push-card__text').textContent = S.done;
          card.querySelector('.push-card__actions').remove();
          if (bell) bell.hidden = true;
          startTracking();
          setTimeout(() => closeCard(false), 3500);
        } catch {
          allow.disabled = false;
        }
      });
      if (!auto) allow.focus();
    }
  };
  const showBell = () => {
    if (bell || (!iosHint && Notification.permission === 'denied')) return;
    bell = document.createElement('button');
    bell.type = 'button';
    bell.className = 'push-bell';
    bell.setAttribute('aria-label', S.bell || 'Notifications');
    bell.setAttribute('aria-haspopup', 'dialog');
    bell.setAttribute('aria-expanded', 'false');
    bell.innerHTML = icon;
    bell.addEventListener('click', () => showCard(false));
    document.body.appendChild(bell);
    requestAnimationFrame(() => bell && bell.classList.add('is-in'));
    // Over the dark footer the ink bell would vanish: it turns gold there
    const footer = document.querySelector('.site-footer');
    if (footer && 'IntersectionObserver' in window) {
      new IntersectionObserver(([e]) => bell && bell.classList.toggle('is-on-dark', e.isIntersecting), { rootMargin: '0px 0px -60px 0px' }).observe(footer);
    }
  };
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && card) closeCard(false); });
  // Klaviyo's sign-up popup opened over an offered card: the card makes way (offered again later)
  window.addEventListener('klaviyoForms', (e) => {
    if (e.detail?.type !== 'open' || !card || !card.dataset.auto) return;
    closeCard(false);
    store.del('harber:push-snooze');
  });

  const offerOnce = () => {
    const snooze = store.get('harber:push-snooze');
    if (snooze && Date.now() - snooze < 14 * 864e5) return;
    if (/\/(cart|checkouts?|account)(\/|$)/.test(location.pathname)) return;
    // Never offered unasked while shopping: not on product, collection or search pages, nor once the bag has items
    // (on desktop the card sat over the buy box and the right-hand product cards). The bell stays everywhere.
    if (/\/(products|collections|search)(\/|$)/.test(location.pathname)) return;
    if (document.querySelector('.cart-count .cart-count__bubble:not(.wish-count)')) return;
    let views = 0;
    try { views = Number(sessionStorage.getItem('harber:push-views') || 0) + 1; sessionStorage.setItem('harber:push-views', String(views)); } catch { views = 2; }
    // Opened from the iPhone Home Screen: they added it to get notifications, so offer at once.
    if (views < (ios && standalone ? 1 : 2)) return;
    setTimeout(() => {
      if (document.querySelector('dialog[open]')) return;
      // Never next to another window: not while the cookie banner waits for an answer, nor on a page view where
      // Klaviyo's sign-up popup opened (theme.js `data-kl-popup`); offered on a later page instead (no snooze set)
      if (document.documentElement.dataset.klPopup || (window.Cookiebot && !window.Cookiebot.hasResponse && document.getElementById('CybotCookiebotDialog')?.getClientRects().length)) return;
      if (!iosHint && (Notification.permission !== 'default' || saved)) return;
      // Offered once per 14 days even if it is ignored (only a dismiss used to snooze it, so it came back on every page)
      store.set('harber:push-snooze', Date.now());
      showCard(true);
    }, 6000);
  };

  // ---- Start (after load, idle: never in the way of the page)
  const start = async () => {
    if (iosHint) { showBell(); offerOnce(); return; }
    const perm = Notification.permission;
    const doneSetup = () => accountSwitch();
    if (perm === 'granted') {
      try {
        const reg = await registration();
        const sub = await reg.pushManager.getSubscription();
        if (store.get('harber:push-off')) {
          // Turned off in the account: stays off although the browser still allows notifications.
          if (sub) await sub.unsubscribe().catch(() => {});
        } else if (!saved || !sub || sub.endpoint !== saved.ep) {
          // Allowed before (PushOwl, or our own subscription lost): move it over silently.
          const migrated = !saved && (await hasPushOwl());
          try {
            await subscribe(migrated ? 'migrated' : 'resubscribe');
          } catch (e) {
            // Reported to Insights → Errors ("web push: …"): on 16 Oct 2026 ~88 browsers began the move and ~22 finished
            document.dispatchEvent(new CustomEvent('push:error', { detail: { step: migrated ? 'migrate' : 'resubscribe', message: `${(e && e.name) || 'Error'}: ${(e && e.message) || e}` } }));
            throw e;
          }
          await retirePushOwl();
        } else if (Date.now() - (saved.t || 0) > 7 * 864e5) {
          saved.t = Date.now();
          store.set(KEY, saved);
          await retirePushOwl();
        }
      } catch { /* blocked by the browser: nothing to do */ }
      startTracking();
      doneSetup();
      return;
    }
    if (saved) { store.del(KEY); saved = null; } // permission revoked in the browser
    if (perm === 'default') {
      showBell();
      offerOnce();
    }
    doneSetup();
  };
  const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 1200));
  let started = false;
  const go = () => { if (!started) { started = true; idle(() => start(), { timeout: 4000 }); } };
  // A browser that allowed notifications but isn't ours yet (a PushOwl subscriber, often just in from one of its
  // notifications) is moved over at once: waiting for "load" + idle (up to ~12 s) lost those who left quickly.
  if (!iosHint && Notification.permission === 'granted' && !saved && !store.get('harber:push-off')) { started = true; start(); }
  else if (document.readyState === 'complete') go();
  else {
    window.addEventListener('load', go, { once: true });
    setTimeout(go, 8000); // a slow third-party tag can hold "load" back for a long time
  }
});
